import styled, { css } from "styled-components";
import { boxShadows, corners, media, typography } from "../../styles";
import { applyOpacity } from "../../styles/utils";
export type ButtonVariant = "destructive" | "primary" | "secondary" | "tertiary";
const DEFAULT_VARIANT = "primary";
const largeButton = css`
  min-height: 2.5em;
  ${typography.bodyBold}
`;
const smallButton = css`
  min-height: 2.25em;
  ${typography.smallBold};

  &:active,
  &:disabled,
  &:focus {
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05, true, theme.brand.cssVars["--d-surface"])};
  }

  @media ${media.hoverable} {
    &:hover {
      background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05, true, theme.brand.cssVars["--d-surface"])};
    }
  }
`;
const BaseButton = styled.button`
  display: block;
  min-width: 0.75rem;

  border: 0;
  ${corners.m}
  &::first-letter {
    text-transform: capitalize;
  }

  &:hover {
    cursor: ${({
  disabled
}) => disabled ? "not-allowed" : "pointer"};
  }

  &:focus {
    outline: none;
  }
`;
const primary = css`
  ${largeButton}
  background-color: ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
  color: ${({
  theme
}) => theme.brand.cssVars["--d-on-action"]};
  transition: filter 0.15s ease-out;

  &:active:not(:disabled) {
    color: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-on-action"], 0.8, false)};
    border: 0;
  }

  &:disabled {
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, theme.colours.isDark.primaryBackground ? 0.4 : 0.1, true, theme.brand.cssVars["--d-surface"])};
    color: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-on-action"], theme.colours.isDark.primaryBackground ? 0.9 : 0.5, true, theme.brand.cssVars["--d-surface"])};
  }

  /*
   * Hover is a pure brightness shift (never a scale/transform), so it reads
   * consistently regardless of what hex the brand's primary colour is.
   */
  @media ${media.hoverable} {
    &:hover:not(:disabled) {
      filter: brightness(0.92);
    }
  }

  &:focus {
    ${boxShadows.focusRing}
  }
`;
const secondary = css`
  ${largeButton}
  border: ${({
  theme
}) => theme.brand.cssVars["--d-border"]} 2px solid;
  background-color: transparent;
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
  transition: filter 0.15s ease-out;

  &:active:not(:disabled) {
    background-color: transparent;
    color: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-text"], 0.8, false)};
  }

  &:disabled {
    border: ${({
  theme
}) => theme.brand.cssVars["--d-border"]} 2px solid;
    background-color: transparent;
    color: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-text"], 0.4, false)};
  }

  @media ${media.hoverable} {
    &:hover:not(:disabled) {
      filter: brightness(0.92);
    }
  }

  &:focus {
    ${boxShadows.focusRing}
  }
`;
const tertiary = css<{
  listItem?: boolean;
}>`
  ${smallButton}
  border: ${({
  listItem,
  theme
}) => listItem ? 0 : theme.brand.cssVars["--d-border"]} solid 2px;
  background-color: transparent;

  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};

  &:disabled {
    border: ${({
  theme
}) => theme.brand.cssVars["--d-border"]} solid 2px;
    color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.5, true, theme.brand.cssVars["--d-surface"])};
  }

  @media ${media.hoverable} {
    &:hover {
      filter: brightness(0.94);
    }
  }

  &:active:not(:disabled) {
    color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
  }

  &:focus {
    ${boxShadows.focusRing}
  }
`;
const destructive = css`
  ${smallButton}
  background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.02, true, theme.brand.cssVars["--d-surface"])};
  color: ${({
  theme
}) => theme.brand.cssVars["--d-error"]};

  &:active:not(:disabled) {
    outline: 1px solid ${({
  theme
}) => theme.brand.cssVars["--d-error"]};
  }

  &:disabled {
    color: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-error"], 0.5, true, theme.brand.cssVars["--d-surface"])};
  }

  &:focus {
    background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.04, true, theme.brand.cssVars["--d-surface"])};
    ${boxShadows.focusRing}
  }
`;
const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  destructive,
  primary,
  secondary,
  tertiary
};
export const Button = styled(BaseButton)<{
  disabled?: boolean;
  listItem?: boolean;
  variant?: ButtonVariant;
}>`
  ${({
  variant
}) => variantStyles[variant ?? DEFAULT_VARIANT]}
`;
export default Button;